/**
 * API 서비스 - Cloud Run FastAPI 서버와 통신
 * 도메인별로 분리된 API 함수들을 통합하여 export
 */

import { apiFetch, postData, putData, deleteData, authenticatedCall } from "./api/base";
import * as filesApi from "./api/files";
import * as conversionApi from "./api/conversion";
import * as libraryAttachApi from "./api/libraryAttach";
import * as viewrApi from "./api/viewr";
import * as jobsApi from "./api/jobs";
import * as storageApi from "./api/storage";
import * as libraryApi from "./api/library";
import * as authApi from "./api/auth";
import * as systemApi from "./api/system";
import * as figmaApi from "./api/figma";
import * as figmaPluginImportsApi from "./api/figmaPluginImports";
import * as multiplayApi from "./api/multiplay";
import * as accessApi from "./api/access";

/**
 * 통합 API 서비스 객체
 * 기존 apiService.js와 동일한 인터페이스를 제공하여 하위 호환성 유지
 */
export const apiService = {
  // Generic fetch method for direct API calls with automatic token inclusion
  fetch: apiFetch,

  // 파일 관련 API
  getSignedUrl: filesApi.getSignedUrl,
  getRawFilesFromStorage: filesApi.getRawFilesFromStorage,
  getSignedUploadUrl: filesApi.getSignedUploadUrl,
  uploadFile: filesApi.uploadFile,
  deleteRawFile: filesApi.deleteRawFile,
  getConvertedFiles: filesApi.getConvertedFiles,
  getMetadataFile: filesApi.getMetadataFile,
  getDirectoryFiles: filesApi.getDirectoryFiles,
  getPlatformFile: filesApi.getPlatformFile,
  uploadThumbnail: filesApi.uploadThumbnail,
  getThumbnailUploadUrl: filesApi.getThumbnailUploadUrl,
  getThumbnailSignedUrl: filesApi.getThumbnailSignedUrl,
  postFigmaHmiThumbnailImmediate: filesApi.postFigmaHmiThumbnailImmediate,
  postProtoPieLibraryThumbnail: filesApi.postProtoPieLibraryThumbnail,

  // 변환 관련 API
  startFileConversion: conversionApi.startFileConversion,
  convertFile: conversionApi.convertFile,
  getConvertStatus: conversionApi.getConvertStatus,
  convertFileJob: conversionApi.convertFileJob,
  getConversionJobStatus: conversionApi.getConversionJobStatus,
  getUserConversionJobs: conversionApi.getUserConversionJobs,
  getConversionJobs: conversionApi.getConversionJobs,
  updateConversionProgress: conversionApi.updateConversionProgress,
  requestFileConversion: conversionApi.requestFileConversion,
  getConvertDownloadUrl: conversionApi.getConvertDownloadUrl,
  createLibraryAttachJob: libraryAttachApi.createLibraryAttachJob,
  attachLibraryVse: libraryAttachApi.attachLibraryVse,
  ensureLibraryVseMetadata: libraryAttachApi.ensureLibraryVseMetadata,

  // ViewR 작업 관련 API
  dispatchViewRJob: viewrApi.dispatchViewRJob,
  createViewR: viewrApi.createViewR,
  listFinalFiles: viewrApi.listFinalFiles,
  recordFinalFileView: viewrApi.recordFinalFileView,
  setFinalFileBookmark: viewrApi.setFinalFileBookmark,
  listSampleFinalFiles: viewrApi.listSampleFinalFiles,
  listSampleConvertFiles: viewrApi.listSampleConvertFiles,
  renameFinalFile: viewrApi.renameFinalFile,
  duplicateFinalFile: viewrApi.duplicateFinalFile,
  deleteFinalFile: viewrApi.deleteFinalFile,
  getFinalDownloadUrl: viewrApi.getFinalDownloadUrl,
  getFinalFilesSignedUploadUrl: viewrApi.getFinalFilesSignedUploadUrl,
  uploadFinalFile: viewrApi.uploadFinalFile,

  // 작업 관리 관련 API (상태 조회는 GET /conversion-job/{job_id} 사용)
  getJobStatus: jobsApi.getJobStatus,
  getUnityStatus: jobsApi.getUnityStatus,
  cancelJob: jobsApi.cancelJob,
  pauseConversionJob: jobsApi.pauseConversionJob,
  resumeConversionJob: jobsApi.resumeConversionJob,

  // 스토리지 및 휴지통 관련 API
  getStorageUsage: storageApi.getStorageUsage,
  deleteFinalFilePermanently: storageApi.deleteFinalFilePermanently,
  bulkMoveFinalFilesToTrash: storageApi.bulkMoveFinalFilesToTrash,
  hideSampleFile: storageApi.hideSampleFile,
  showSampleFile: storageApi.showSampleFile,
  moveFileToTrash: storageApi.moveFileToTrash,
  restoreFileFromTrash: storageApi.restoreFileFromTrash,
  listTrashFiles: storageApi.listTrashFiles,
  cleanupExpiredTrashFiles: storageApi.cleanupExpiredTrashFiles,

  // Library 관련 API (DB 기반)
  getLibraryItems: libraryApi.getLibraryItems,
  getLibraryItemDetail: libraryApi.getLibraryItemDetail,
  renameLibraryItem: libraryApi.renameLibraryItem,
  syncLibraryFromGcs: libraryApi.syncLibraryFromGcs,
  syncAllFromGcs: libraryApi.syncAllFromGcs,
  deleteLibraryItem: libraryApi.deleteLibraryItem,
  bulkDeleteLibraryItems: libraryApi.bulkDeleteLibraryItems,
  bulkDeleteFigmaPluginImports: figmaPluginImportsApi.bulkDeleteFigmaPluginImports,

  // 인증 및 사용자 관련 API
  saveUserInfo: authApi.saveUserInfo,
  getUserInfo: authApi.getUserInfo,
  getAllUsers: authApi.getAllUsers,
  blockUser: authApi.blockUser,
  unblockUser: authApi.unblockUser,
  blockUserByEmail: authApi.blockUserByEmail,
  unblockUserByEmail: authApi.unblockUserByEmail,
  deleteAccount: authApi.deleteAccount,
  getIdToken: authApi.getIdToken,

  // Studio access approval
  getAccessStatus: accessApi.getAccessStatus,
  createAccessRequest: accessApi.createAccessRequest,

  // 시스템 및 디버그 관련 API
  getHealth: systemApi.getHealth,
  getItem: systemApi.getItem,
  getServerStatus: systemApi.getServerStatus,
  debugValidateUser: systemApi.debugValidateUser,
  debugRawData: systemApi.debugRawData,
  testDatabaseConnection: systemApi.testDatabaseConnection,
  saveUserInfoTest: systemApi.saveUserInfoTest,
  saveUserInfoToFile: systemApi.saveUserInfoToFile,
  callComputeEngineHealth: systemApi.callComputeEngineHealth,
  callComputeEngineEcho: systemApi.callComputeEngineEcho,
  testComputeEngineHealth: systemApi.testComputeEngineHealth,
  testComputeEngineEcho: systemApi.testComputeEngineEcho,
  debugEnvironment: systemApi.debugEnvironment,
  testUnityConnection: systemApi.testUnityConnection,
  prepareMultiplayRoom: multiplayApi.prepareMultiplayRoom,
  registerMultiplayRoom: multiplayApi.registerMultiplayRoom,
  joinMultiplayRoom: multiplayApi.joinMultiplayRoom,
  leaveMultiplayRoom: multiplayApi.leaveMultiplayRoom,
  getMultiplayRoomByShareCode: multiplayApi.getMultiplayRoomByShareCode,
  requestMultiplayRoomDownloadUrl: multiplayApi.requestMultiplayRoomDownloadUrl,
  getMultiplaySessionStatus: multiplayApi.getMultiplaySessionStatus,
  relayMultiplayCommand: multiplayApi.relayMultiplayCommand,

  // 공통 HTTP 메서드 헬퍼
  postData,
  putData,
  deleteData,
  authenticatedCall,
};

export default apiService;
